/**
 * HQ 공지 관리 — 본부가 점주에게 보내는 공지 등록/수정/삭제.
 * 게시(is_active)하면 점주 로그인 팝업(show_popup) + 대시보드(show_dashboard)에 노출.
 */
import React, { useEffect, useState } from 'react';
import { hqNoticeService, HqNotice, HqNoticeInput } from '../services/hqNoticeService';
import { hqAuthService } from '../services/hqAuthService';

const EMPTY: HqNoticeInput = {
  title: '',
  body: '',
  version: '',
  is_active: true,
  show_popup: true,
  show_dashboard: true,
  is_important: false,
};

const HqNoticesPage: React.FC = () => {
  const isAdmin = hqAuthService.getCachedUser()?.role === 'hq_admin';
  const [list, setList] = useState<HqNotice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null); // null=미편집, 'new'=신규
  const [form, setForm] = useState<HqNoticeInput>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState<HqNotice | null>(null); // 내용 보기 모달

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setList(await hqNoticeService.list());
    } catch (e: any) {
      setError(e?.response?.data?.detail || '공지를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setForm(EMPTY);
    setEditingId('new');
  };
  const startEdit = (n: HqNotice) => {
    setForm({
      title: n.title,
      body: n.body,
      version: n.version || '',
      is_active: n.is_active,
      show_popup: n.show_popup,
      show_dashboard: n.show_dashboard,
      is_important: !!n.is_important,
    });
    setEditingId(n.id);
  };
  const cancel = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const save = async () => {
    if (!form.title.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }
    setSaving(true);
    try {
      if (editingId === 'new') {
        await hqNoticeService.create(form);
      } else if (editingId) {
        await hqNoticeService.update(editingId, form);
      }
      cancel();
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (n: HqNotice) => {
    if (!window.confirm(`"${n.title}" 공지를 삭제할까요?`)) return;
    try {
      await hqNoticeService.remove(n.id);
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || '삭제 실패');
    }
  };

  const toggleActive = async (n: HqNotice) => {
    try {
      await hqNoticeService.update(n.id, { is_active: !n.is_active });
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || '변경 실패');
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800">📢 공지 관리</h1>
          <p className="text-sm text-slate-500 mt-1">
            게시하면 점주 <b>로그인 팝업</b>과 <b>대시보드</b>에 표시됩니다.
          </p>
        </div>
        {isAdmin && !editingId && (
          <button
            onClick={startNew}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg"
          >
            + 새 공지
          </button>
        )}
      </div>

      {/* 편집 폼 */}
      {editingId && (
        <div className="mb-6 rounded-lg border border-slate-200 bg-white p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">제목</label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="예: 2026년 7월 업데이트 안내"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              본문 <span className="text-slate-400">(줄바꿈 그대로 표시 — 기능 소개 + 사용법)</span>
            </label>
            <textarea
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              rows={14}
              placeholder={'예)\n🆕 새 기능\n1. 카톡 알림톡 미리보기 — 연동설정 › 알림톡 설정에서 …\n\n사용법: …'}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono whitespace-pre"
            />
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <label className="text-sm text-slate-700">
              라벨(선택)
              <input
                value={form.version || ''}
                onChange={(e) => setForm({ ...form, version: e.target.value })}
                placeholder="2026-07"
                className="ml-2 border border-slate-300 rounded-md px-2 py-1 text-sm w-32"
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              게시(활성)
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!form.show_popup}
                onChange={(e) => setForm({ ...form, show_popup: e.target.checked })} />
              로그인 팝업
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!form.show_dashboard}
                onChange={(e) => setForm({ ...form, show_dashboard: e.target.checked })} />
              대시보드 공지
            </label>
            {/* 2026-10-08 — 원장 대시보드 왼쪽 '중요 공지' 칸. 끄면 오른쪽 '일반 공지' 칸 */}
            <label className="flex items-center gap-2 text-sm text-red-700 font-medium">
              <input type="checkbox" checked={!!form.is_important}
                onChange={(e) => setForm({ ...form, is_important: e.target.checked })} />
              중요 공지 (대시보드 왼쪽 칸)
            </label>
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={save} disabled={saving}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50">
              {saving ? '저장 중…' : '저장'}
            </button>
            <button onClick={cancel}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg">
              취소
            </button>
          </div>
        </div>
      )}

      {/* 목록 */}
      {loading ? (
        <div className="text-slate-400">불러오는 중…</div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">{error}</div>
      ) : list.length === 0 ? (
        <div className="text-slate-400 py-8 text-center">등록된 공지가 없습니다.</div>
      ) : (
        <div className="space-y-3">
          {list.map((n) => (
            <div key={n.id} className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 cursor-pointer group" onClick={() => setViewing(n)} title="클릭하면 전체 내용 보기">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800 group-hover:underline">{n.title}</span>
                    {n.version && <span className="text-xs text-slate-400">[{n.version}]</span>}
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${
                      n.is_active ? 'bg-green-50 text-green-700 border-green-200'
                        : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                      {n.is_active ? '게시중' : '숨김'}
                    </span>
                    {n.show_popup && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">팝업</span>}
                    {n.show_dashboard && <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">대시보드</span>}
                    {n.is_important && <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">중요</span>}
                  </div>
                  <p className="text-sm text-slate-500 mt-1 line-clamp-2 whitespace-pre-line">{n.body}</p>
                  <span className="text-xs text-blue-500 mt-1 inline-block group-hover:underline">내용 전체 보기 →</span>
                </div>
                {isAdmin && (
                  <div className="flex flex-shrink-0 items-center gap-2">
                    <button onClick={() => toggleActive(n)}
                      className="text-xs text-slate-600 hover:text-slate-900 underline">
                      {n.is_active ? '숨기기' : '게시'}
                    </button>
                    <button onClick={() => startEdit(n)}
                      className="text-xs text-blue-600 hover:text-blue-800 underline">수정</button>
                    <button onClick={() => remove(n)}
                      className="text-xs text-red-500 hover:text-red-700 underline">삭제</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 내용 보기 모달 */}
      {viewing && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setViewing(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-slate-800">{viewing.title}</h2>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {viewing.version && <span className="text-xs text-slate-400">[{viewing.version}]</span>}
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${
                    viewing.is_active ? 'bg-green-50 text-green-700 border-green-200'
                      : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                    {viewing.is_active ? '게시중' : '숨김'}
                  </span>
                  {viewing.show_popup && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">팝업</span>}
                  {viewing.show_dashboard && <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">대시보드</span>}
                  {viewing.is_important && <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">중요</span>}
                </div>
              </div>
              <button onClick={() => setViewing(null)}
                className="text-slate-400 hover:text-slate-700 text-2xl leading-none flex-shrink-0">×</button>
            </div>

            <div className="mt-4 border-t border-slate-100 pt-4 whitespace-pre-line text-sm text-slate-700 leading-relaxed">
              {viewing.body ? viewing.body : <span className="text-slate-400">(내용 없음)</span>}
            </div>

            {viewing.updated_at && (
              <div className="mt-4 text-xs text-slate-400">
                최근 수정: {new Date(viewing.updated_at).toLocaleString('ko-KR')}
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              {isAdmin && (
                <button
                  onClick={() => { const n = viewing; setViewing(null); startEdit(n); }}
                  className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                >
                  수정
                </button>
              )}
              <button onClick={() => setViewing(null)}
                className="px-4 py-2 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg">
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HqNoticesPage;
